/**
 * Value equality for two copies of one document field.
 *
 * @module utils/same-value
 */

import { stableJson } from "@/utils/stable-json";

/**
 * Whether a field value is one of the spellings of "not set".
 *
 * A document reaches its readers by different routes: the editor holds an
 * explicit `null` for an unset field the GraphQL selection names, the wire
 * drops that `null` on the way back, and a cleared text input holds `""`.
 * Comparing copies that crossed different routes has to read all three as
 * one absent value.
 *
 * @param value - The field value.
 * @returns Whether it is absent, null or empty.
 */
export function unset(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Value equality that ignores key order and the spelling of "not set".
 *
 * The one comparison every side of a module save uses, so the editor, the
 * conflict panel and the server agree on what counts as a change. A value
 * JSON cannot carry, such as a Date or a non-finite number, is never the same
 * as anything but itself, since a document holding one is already wrong.
 *
 * @param left - One value.
 * @param right - The other.
 * @returns Whether they are the same value.
 */
export function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }
  if (unset(left) || unset(right)) {
    return unset(left) && unset(right);
  }
  try {
    return stableJson(left, { validate: true }) === stableJson(right, { validate: true });
  } catch {
    return false;
  }
}
